import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import localForage from "localforage";

const ACCESS_TOKEN_STORAGE_KEY = "aegis360.accessToken";
const REFRESH_TOKEN_STORAGE_KEY = "aegis360.refreshToken";

// Configure localForage for robust offline storage
localForage.config({
  name: "PestControlTechApp",
  storeName: "technicianCache",
  description:
    "Offline cache for technician app - appointments, reports, and data",
});

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

export function setAuthTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
}

export function clearAuthTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
}

function buildAuthHeaders(baseHeaders: Record<string, string> = {}) {
  const headers = { ...baseHeaders };
  const accessToken = getAccessToken();

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
}

function canAttemptRefresh(url: string) {
  return !url.includes("/api/auth/login") && !url.includes("/api/auth/refresh");
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearAuthTokens();
      return false;
    }

    const payload = await response.json();
    if (payload?.accessToken && payload?.refreshToken) {
      setAuthTokens(payload.accessToken, payload.refreshToken);
      return true;
    }

    clearAuthTokens();
    return false;
  } catch {
    return false;
  }
}

async function authFetch(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const response = await fetch(url, {
    ...init,
    headers: buildAuthHeaders((init.headers as Record<string, string>) || {}),
    credentials: "include",
  });

  if (response.status !== 401 || !canAttemptRefresh(url)) {
    return response;
  }

  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    return response;
  }

  return fetch(url, {
    ...init,
    headers: buildAuthHeaders((init.headers as Record<string, string>) || {}),
    credentials: "include",
  });
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await authFetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await authFetch(queryKey.join("/") as string);

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export async function logout(): Promise<void> {
  try {
    await authFetch("/api/auth/logout", {
      method: "POST",
    });
  } catch {
    // Ignore logout network failures, local session is still cleared.
  } finally {
    clearAuthTokens();
    queryClient.clear();
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "returnNull" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000, // 10 minutes - longer cache for better performance
      gcTime: 24 * 60 * 60 * 1000, // 24 hours for offline access
      retry: (failureCount, error: any) => {
        // Enhanced offline detection for mobile browsers
        const isNetworkError =
          !navigator.onLine ||
          error?.message?.includes("Failed to fetch") ||
          error?.name === "NetworkError" ||
          error instanceof TypeError ||
          error?.code === "NETWORK_ERR";

        if (isNetworkError) {
          return failureCount < 3;
        }
        return false;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      networkMode: "offlineFirst", // Try cache first, then network
    },
    mutations: {
      retry: (failureCount, error: any) => {
        // Enhanced offline detection for mutations with higher retry count
        const isNetworkError =
          !navigator.onLine ||
          error?.message?.includes("Failed to fetch") ||
          error?.name === "NetworkError" ||
          error instanceof TypeError ||
          error?.code === "NETWORK_ERR";

        if (isNetworkError) {
          return failureCount < 5;
        }
        return false;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      networkMode: "offlineFirst",
    },
  },
});

// Create persister for offline storage using IndexedDB (async)
const persister = createAsyncStoragePersister({
  storage: localForage,
  serialize: JSON.stringify,
  deserialize: JSON.parse,
});

// Persist query client for offline functionality
// This ensures data survives app reloads and device sleep
persistQueryClient({
  queryClient,
  persister,
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
  buster: "v1.1.0", // Increment to clear cache when schema changes
});
